/* eslint-disable react/prop-types -- project uses JSX without PropTypes package */
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import EditableBrandTag from "./EditableBrandTag";
import {
  isDuplicateBrandName,
  normalizeBrandName,
} from "../../helpers/sizingChartLocal";
import PlusIcon from "../../assets/svg/icon_plus.svg?react";

/**
 * Inline brand tags + add control. Parent owns persistence.
 */
export default function BrandTagContainer({
  brands,
  onCreateBrand,
  onRenameBrand,
  disabled = false,
  onDeleteRequest,
}) {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState("");
  const addInputRef = useRef(null);
  /** Ignore blur right after opening the add field — spurious blur would commit empty and close. */
  const ignoreAddBlurUntil = useRef(0);

  const commitAdd = () => {
    if (Date.now() < ignoreAddBlurUntil.current) {
      return;
    }
    const name = normalizeBrandName(addDraft);
    if (!name) {
      setAdding(false);
      setAddDraft("");
      return;
    }
    if (isDuplicateBrandName(brands, name)) {
      toast.error("A brand with this name already exists.");
      setAdding(false);
      setAddDraft("");
      return;
    }
    onCreateBrand({ name });
    setAddDraft("");
    setAdding(false);
  };

  const commitRename = (id, raw) => {
    const name = normalizeBrandName(raw);
    if (!name) {
      setEditingId(null);
      return;
    }
    if (isDuplicateBrandName(brands, name, id)) {
      toast.error("A brand with this name already exists.");
      setEditingId(null);
      return;
    }
    onRenameBrand({ id, name });
    setEditingId(null);
  };

  const startAdd = () => {
    setAdding(true);
    setAddDraft("");
    ignoreAddBlurUntil.current = Date.now() + 300;
    requestAnimationFrame(() => {
      addInputRef.current?.focus();
    });
  };

  if (!brands.length && !isAdding) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
        <p className="text-base font-semibold text-[var(--clr-text-title)] mb-2">
          No brands yet
        </p>
        <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
          Add at least one brand before you can create sizing charts. Brands can
          be edited inline and removed when they are not in use.
        </p>
        <button
          type="button"
          className="btn-solid min-h-[44px] px-6"
          onMouseDown={(e) => e.preventDefault()}
          onClick={startAdd}
          disabled={disabled}
        >
          Add brand
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {brands.map((b) => (
        <EditableBrandTag
          key={b.id}
          brand={b}
          isEditing={editingId === b.id}
          onBeginEdit={() => setEditingId(b.id)}
          onCommit={(draft) => commitRename(b.id, draft)}
          onCancel={() => setEditingId(null)}
          onDeleteClick={() => onDeleteRequest(b)}
          disabled={disabled}
        />
      ))}

      {isAdding ? (
        <span className="inline-flex items-center rounded-full border border-primary border-dashed bg-white pl-3 pr-2 py-1 text-sm">
          <input
            ref={addInputRef}
            className="outline-none min-w-[120px] max-w-[200px] text-slate-800"
            placeholder="Brand name"
            value={addDraft}
            onChange={(e) => setAddDraft(e.target.value)}
            onBlur={() => commitAdd()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitAdd();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setAdding(false);
                setAddDraft("");
              }
            }}
            aria-label="New brand name"
          />
        </span>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={startAdd}
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary transition-colors"
        >
          <PlusIcon className="w-4 h-4" aria-hidden />
          Add brand
        </button>
      )}
    </div>
  );
}
